<template>
  <div>
    <Header />
  
  <v-container fluid>
    <v-card>
      <v-card-title>
        <v-icon left>mdi-inbox</v-icon>
        Nachrichten
        <v-spacer></v-spacer>
        <v-chip v-if="unreadCount > 0" color="primary" text-color="white" small>
          {{ unreadCount }} ungelesen
        </v-chip>
      </v-card-title>

      <v-card-text>
        <!-- Filters -->
        <v-row class="mb-4">
          <v-col cols="12" md="3">
            <v-text-field
              v-model="filters.searchTerm"
              label="Suche"
              placeholder="Titel oder Inhalt durchsuchen..."
              prepend-inner-icon="mdi-magnify"
              clearable
              dense
              outlined
              @input="debounceSearch"
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="3">
            <v-select
              v-model="filters.importance"
              :items="importanceOptions"
              label="Wichtigkeit"
              clearable
              dense
              outlined
              @change="handleFilterChange"
            ></v-select>
          </v-col>
          <v-col cols="12" md="3">
            <v-checkbox
              v-model="filters.unreadOnly"
              label="Nur ungelesene anzeigen"
              @change="handleFilterChange"
            ></v-checkbox>
          </v-col>
          <v-col cols="12" md="3" class="text-right">
            <v-btn
              color="primary"
              @click="fetchMessages"
              :loading="loading"
            >
              <v-icon left>mdi-refresh</v-icon>
              Aktualisieren
            </v-btn>
          </v-col>
        </v-row>

        <!-- Messages List -->
        <v-list three-line v-if="!loading && messages.length > 0">
          <div v-for="(message, index) in messages" :key="message._id">
            <v-list-item
              @click="openMessage(message)"
              :class="{ 'grey lighten-4': !message.isRead }"
            >
              <v-list-item-avatar>
                <v-icon 
                  :color="getImportanceColor(message.importance)"
                  size="40"
                >
                  {{ getImportanceIcon(message.importance) }}
                </v-icon>
              </v-list-item-avatar>

              <v-list-item-content>
                <v-list-item-title class="font-weight-medium">
                  {{ message.title }}
                  <v-chip 
                    v-if="!message.isRead" 
                    x-small 
                    color="primary" 
                    text-color="white"
                    class="ml-2"
                  >
                    NEU
                  </v-chip>
                </v-list-item-title>
                <v-list-item-subtitle 
                  v-html="getPreview(message.content)"
                  class="message-preview"
                ></v-list-item-subtitle>
                <v-list-item-subtitle class="mt-1">
                  <v-icon small>mdi-clock-outline</v-icon>
                  {{ formatDate(message.createdAt) }}
                </v-list-item-subtitle>
              </v-list-item-content>

              <v-list-item-action>
                <v-chip
                  :color="getImportanceColor(message.importance)"
                  text-color="white"
                  small
                >
                  {{ getImportanceLabel(message.importance) }}
                </v-chip>
              </v-list-item-action>
            </v-list-item>

            <v-divider
              v-if="index < messages.length - 1"
            ></v-divider>
          </div>
        </v-list>

        <!-- Empty State -->
        <v-alert
          v-else-if="!loading && messages.length === 0"
          type="info"
          text
          prominent
          icon="mdi-inbox-outline"
        >
          Keine Nachrichten vorhanden
        </v-alert>

        <!-- Loading State -->
        <div v-if="loading" class="text-center py-8">
          <v-progress-circular
            indeterminate
            color="primary"
            size="64"
          ></v-progress-circular>
        </div>
      </v-card-text>
    </v-card>

    <!-- Message Dialog -->
    <v-dialog
      v-model="messageDialog"
      max-width="800"
      scrollable
    >
      <v-card v-if="selectedMessage">
        <v-card-title
          :style="{
            backgroundColor: selectedMessage.backgroundColor || '#1976D2',
            color: selectedMessage.textColor || '#FFFFFF'
          }"
        >
          {{ selectedMessage.title }}
          <v-spacer></v-spacer>
          <v-btn
            icon
            :color="selectedMessage.textColor || '#FFFFFF'"
            @click="messageDialog = false"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pa-4">
          <div class="message-meta mb-4">
            <v-chip
              :color="getImportanceColor(selectedMessage.importance)"
              text-color="white"
              small
              class="mr-2"
            >
              {{ getImportanceLabel(selectedMessage.importance) }}
            </v-chip>
            <span class="grey--text">
              <v-icon small>mdi-clock-outline</v-icon>
              {{ formatDate(selectedMessage.createdAt) }}
            </span>
          </div>
          
          <div 
            class="message-content"
            v-html="selectedMessage.content"
          ></div>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            text
            @click="messageDialog = false"
          >
            Schließen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import MessageService from '@/services/MessageService';
import Header from '@/views/private/header.vue';

export default {
  name: 'MessageInbox',
  
  components: {
    Header
  },
  
  data() {
    return {
      messageDialog: false,
      selectedMessage: null,
      filters: {
        importance: null,
        unreadOnly: false,
        searchTerm: ''
      },
      searchTimeout: null,
      importanceOptions: [
        { text: 'Alle', value: null },
        { text: 'Niedrig', value: 'low' },
        { text: 'Mittel', value: 'medium' },
        { text: 'Hoch', value: 'high' },
        { text: 'Kritisch', value: 'critical' }
      ]
    };
  },

  computed: {
    ...mapState('messages', ['loading']),
    ...mapGetters('messages', ['allMessages', 'unreadCount']),
    
    messages() {
      return this.allMessages;
    }
  },

  methods: {
    ...mapActions('messages', [
      'fetchMessages',
      'markAsRead',
      'updateFilters'
    ]),

    handleFilterChange() {
      // Update filters in store with local filter values
      this.updateFilters(this.filters);
    },

    debounceSearch() {
      // Clear existing timeout
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      
      // Set new timeout for search
      this.searchTimeout = setTimeout(() => {
        this.handleFilterChange();
      }, 300); // 300ms delay
    },

    openMessage(message) {
      this.selectedMessage = message;
      this.messageDialog = true;
      
      if (!message.isRead) {
        this.markAsRead(message._id);
      }
    },

    getPreview(content) {
      // Remove HTML tags and limit to 150 characters
      const text = content.replace(/<[^>]*>/g, '');
      return text.length > 150 ? text.substring(0, 150) + '...' : text;
    },

    getImportanceColor(importance) {
      return MessageService.getImportanceColor(importance);
    },

    getImportanceLabel(importance) {
      return MessageService.getImportanceLabel(importance);
    },

    getImportanceIcon(importance) {
      const icons = {
        low: 'mdi-information-outline',
        medium: 'mdi-alert-circle-outline',
        high: 'mdi-alert',
        critical: 'mdi-alert-octagon'
      };
      return icons[importance] || 'mdi-message-outline';
    },

    formatDate(date) {
      return MessageService.formatDate(date);
    }
  },

  mounted() {
    this.fetchMessages();
  }
};
</script>

<style scoped>
.message-preview {
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  line-height: 1.4em;
  max-height: 2.8em;
}

.message-content {
  font-size: 16px;
  line-height: 1.6;
}

.message-content >>> p {
  margin-bottom: 1em;
}

.message-content >>> ul,
.message-content >>> ol {
  margin-left: 20px;
  margin-bottom: 1em;
}

.message-meta {
  display: flex;
  align-items: center;
  padding-bottom: 16px;
  border-bottom: 1px solid #e0e0e0;
}

.v-list-item:hover {
  background-color: #f5f5f5;
  cursor: pointer;
}
</style>